import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import { gtmMiddleware } from './Middlewares/Gtm.middleware';
import LiveEditing from './Components/LiveEditing';
import MiniCartContainer from './Containers/MiniCart.container';
import QuickSearchContainer from './Containers/QuickSearch.container';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import DynamicComponent from './Components/DynamicComponent';
import ProductConfiguratorContainer from './Containers/ProductConfigurator.container';
import AddonProductContainer from './Containers/AddonProduct.container';
import { ExpandableCategoryDescription } from '../Scripts/Vanilla/CategoryDescription';
import '../Scripts/Vanilla/GtmEvents';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = window.__litium.store = window.__litium.store || (window.parent.__litium && window.parent.__litium.store)
    || createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware, gtmMiddleware)));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
}

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
    });
}

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCartContainer />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearchContainer />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <NavigationContainer />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('navbar-menu-header-hult')) {
        $("#nav-switch-hult").clone().prependTo("#navbar-menu-header-hult")
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchContainer />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Containers/PersonList.container')
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Containers/AddressList.container')
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout')
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Containers/LightboxImages.container')
        });
        const rootElement = document.getElementById('lightBoxImages');
        const { title, classes, tags } = rootElement.dataset;
        const images = Array.from(rootElement.querySelectorAll('a')).map(img => ({ src: img.dataset.src }));
        const thumbnails = Array.from(rootElement.querySelectorAll('a img')).map(img => ({ src: img.src }));
        const tagList = JSON.parse(tags);

        renderReact(
            <Provider store={store}>
                <LightboxImages images={images} thumbnails={thumbnails} title={title} classes={classes} tags={tagList} />
            </Provider>,
            document.getElementById('lightBoxImages')
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider')
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const values = [...slider.querySelectorAll('.slider__block')].map(block => {
                return ({
                    image: block.dataset.image,
                    mobileImage: block.dataset.mobileImage,
                    header: block.dataset.header,
                    subHeader: block.dataset.subHeader,
                    url: block.dataset.url,
                    text: block.dataset.text,
                    tint: block.dataset.tint,
                    textColor: block.dataset.textColor
                })
            });
            if (values.length > 0) {
                renderReact(
                    <Slider values={values} />,
                    slider
                );
            }
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Containers/BuyButton.container')
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { articleNumber, price, quantityFieldId, href, cssClass, label, currency } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <BuyButton {...{ label, articleNumber, price, quantityFieldId, href, cssClass, currency }} />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.container')
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach((button) => {
            const { cssClass, orderId, title } = button.dataset;
            const label = button.innerText;
            renderReact(
                <Provider store={store}>
                    <ReorderButton {...{ label, title, cssClass, orderId }} />
                </Provider>,
                button
            );
        });
    }
    if (document.getElementById('ProductConfigurator')) {

        const configurator = document.getElementById('ProductConfigurator');

        console.log("Configuration product");
        const { baseProduct, cssClass, label, labelEmpty, currency } = configurator.dataset;

        renderReact(
            <Provider store={store}>
                <ProductConfiguratorContainer {...{ baseProduct, cssClass, label, labelEmpty, currency }} />
            </Provider>,
            configurator
        );
    }
    if (!!document.querySelectorAll('#AddonProduct').length) {
        const configurators = document.querySelectorAll('#AddonProduct');

        Array.from(configurators).forEach((configurator) => {
            const { baseProduct, cssClass, label, canBuy, product, variantPrice, listPrice, statusCode, statusText, currency } = configurator.dataset;

            renderReact(
                <Provider store={store}>
                    <AddonProductContainer {...{ baseProduct, cssClass, label, canBuy, product, variantPrice, listPrice, statusCode, statusText, currency }} />
                </Provider>,
                configurator
            );
        })
    }
    if (document.getElementById('brandsListNavigation')) {
        const data_str = document.getElementById('brandsListNavigation').dataset.letters;
        const letters = JSON.parse(decodeURIComponent(data_str));
        const BrandsListNavigation = DynamicComponent({
            loader: () => import('./Components/BrandsListNavigation')
        });
        renderReact(
            <Provider store={store}>
                <BrandsListNavigation letters={letters}/>
            </Provider>,
            document.getElementById('brandsListNavigation')
        );
    }
    if (document.querySelectorAll('#lipscore-service-reviews').length > 0) {
        const HeaderServiceReviews = DynamicComponent({
            loader: () => import('./Components/HeaderServiceReviews')
        });
        Array.from(document.querySelectorAll('#lipscore-service-reviews')).forEach((container) => {
            renderReact(
                <Provider store={store}>
                    <HeaderServiceReviews />
                </Provider>,
                container,
            );
        });
    }

    const footerMenuColumns = document.querySelectorAll('[data-component="footer-menu-column"]');
    if (!!footerMenuColumns.length) {
        const FooterMenuColumn = DynamicComponent({
            loader: () => import('./Components/FooterMenuColumn')
        });
        Array.from(footerMenuColumns).forEach((container) => {
            try {
                const data = JSON.parse(container.querySelector('json-data').innerHTML);

                renderReact(
                    <Provider store={store}>
                        <FooterMenuColumn {...data} />
                    </Provider>,
                    container,
                );
            } catch (error) {
                console.error(error);
            }
        });
    }

    if (document.querySelector('.category-page__intro-description')){
        ExpandableCategoryDescription()
    }

    const stockStatusContainer = document.getElementById('stock-status-container');
    if (stockStatusContainer) {
        const { status, statusText, storeStatus } = stockStatusContainer.dataset;

        const StockStatus = DynamicComponent({
            loader: () => import('./Components/ProductStockStatus')
        });
        renderReact(
            <Provider store={store}>
                <StockStatus status={status} statusText={statusText} storeStatus={storeStatus} />
            </Provider>,
            stockStatusContainer
        );
    }
}

bootstrapComponents();

document.addEventListener('DOMContentLoaded', () => {

    const searchIcon = document.querySelector('.search-icon');
    if (searchIcon != null) {
        searchIcon.addEventListener('click', (evt) => {
            document.querySelector('#quicksearch .addwish-search-input').click();
        });
    }

    if (document.getElementById('moreinfobutton') != null) {
        document.getElementById('moreinfobutton').addEventListener('click', (evt) => {
            evt.preventDefault();
            window.scrollTo({
                left: 0,
                top: window.pageYOffset + document.querySelector('.product-info__container').getBoundingClientRect().top - 150,
                behavior: 'smooth'
            });
        });
    }

    var clickEvent = (evt) => {
        evt.preventDefault();
        if (document.querySelector('.category-info-block') != null) {
            window.scrollTo({
                left: 0,
                top: window.pageYOffset + document.querySelector('.category-info-block').getBoundingClientRect().top - 50,
                behavior: 'smooth'
            });
        }
    };

    if (document.querySelector('.readmore-link') != null) {
        document.querySelectorAll('.readmore-link').forEach(function (e) {
            e.addEventListener('click', clickEvent);
        });
    }

    if (document.getElementById('ProductConfigurator')) {
        document.getElementsByTagName('html')[0].addEventListener('click', function (evt) {

            if (!evt.target.classList.contains('variants')) {
                document.querySelectorAll('.variant-holder .variants').forEach(function (e) {
                    e.parentNode.firstChild.classList.remove('active');
                    e.style.display = 'none';
                });
            }
        });
    }

    if (document.getElementById('miniCart')) {
        document.getElementsByTagName('html')[0].addEventListener('click', function (evt) {

            var cart = document.querySelector('.cart__info');
            if (!cart.classList.contains('cart__info--hidden')) {
                if (!evt.target.classList.contains('cart__info')) {

                    var target = evt.target;
                    if (!target.classList.contains('bag')) {
                        var attempts = 0;
                        while (target.classList != null && !target.classList.contains('bag')) {
                            target = target.parentNode;
                            ++attempts;
                            if (attempts >= 10) {
                                break;
                            }
                        }
                    }

                    if (target.classList == null || !target.classList.contains('bag')) {
                        document.querySelectorAll('.cart__info').forEach(function (e) {
                            e.parentNode.childNodes[0].click();
                        });
                    }
                }
            }
        });
    }

    window.addEventListener('scroll', function () {
        if (window.scrollY >= 75) {
            document.getElementById('header').classList.add('scrolled-nav');
        } else {
            document.getElementById('header').classList.remove('scrolled-nav');
        }
    });

    document.addEventListener('click', function (e) {
        for (var target = e.target; target && target != this; target = target.parentNode) {
            if (target.matches('.hr-search-overlay-product')) {
                fbq('track', 'Search');
                break;
            }
        }
    }, false);
});


